/**
 * Sentry — Lazy-loaded wrapper.
 *
 * The Sentry SDK (~150–200 KB gzip including browserTracing + replay) is
 * NOT included in the main bundle. It loads asynchronously after first paint,
 * keeping cold-start fast. All public functions (captureError, setSentryUser,
 * addNavigationBreadcrumb) keep their synchronous signatures: calls made
 * before Sentry is loaded are queued and flushed once the SDK is ready.
 */

import { isNativePlatform } from "./capacitor-safe";

type SentryModule = typeof import("@sentry/react");

let sentryMod: SentryModule | null = null;
let loadingPromise: Promise<SentryModule | null> | null = null;
let initialized = false;

type BreadcrumbLevel = "info" | "warning" | "error" | "debug";
type EventLevel = "fatal" | "error" | "warning" | "info" | "debug";

interface BreadcrumbPayload {
  category: string;
  message: string;
  level?: BreadcrumbLevel;
  data?: Record<string, unknown>;
}

interface EventPayload {
  message: string;
  level?: EventLevel;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
}

type QueuedCall =
  | { kind: "captureError"; error: unknown; context?: Record<string, unknown> }
  | { kind: "setUser"; userId: string | null }
  | { kind: "breadcrumb"; from: string; to: string }
  | { kind: "customBreadcrumb"; payload: BreadcrumbPayload }
  | { kind: "captureEvent"; payload: EventPayload };

const queue: QueuedCall[] = [];

/**
 * Sentry is WEB ONLY.
 *
 * Two independent gates, both of which must pass:
 *  1. Native guard — if the app runs inside Capacitor (iOS/Android) the SDK is
 *     never loaded, even if VITE_SENTRY_DSN is configured later. This is what
 *     backs the App Store privacy label "Diagnostics — Not Collected".
 *  2. DSN gate — no DSN configured means no Sentry anywhere (current state).
 */
function shouldLoad(): boolean {
  if (isNativePlatform()) return false;
  return !!import.meta.env.VITE_SENTRY_DSN;
}

async function loadSentry(): Promise<SentryModule | null> {
  if (sentryMod) return sentryMod;
  if (!shouldLoad()) return null;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    try {
      const mod = await import("@sentry/react");
      sentryMod = mod;
      runInit(mod);
      flushQueue(mod);
      return mod;
    } catch (e) {
      console.warn("[Sentry] failed to load SDK", e);
      return null;
    }
  })();

  return loadingPromise;
}

function runInit(Sentry: SentryModule) {
  if (initialized) return;
  initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const releaseVersion = import.meta.env.VITE_APP_VERSION;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: releaseVersion ? `mblearn@${releaseVersion}` : undefined,
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Privacy (FASE 1): Session Replay must never capture question text,
      // answers, emails or any typed input. Everything is masked/blocked.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
        networkCaptureBodies: false,
      }),
    ],

    ignoreErrors: [
      "ResizeObserver loop",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "AbortError",
      // Removed: "Network request failed", "Failed to fetch", "Load failed"
      // These were masking real RPC/edge-function failures. We want visibility
      // on backend errors even though they bundle in occasional flaky-network noise.
      //
      // Removed (#48): "ChunkLoadError". This was hiding failures of the
      // version-watcher reload path — if a hard reload after a deploy fails
      // because a chunk 404s, we need to see it in Sentry, not swallow it.
    ],
    sendDefaultPii: false,
    // Privacy (FASE 1): strip any e-mail / free text that could reach Sentry
    // through breadcrumbs (fetch/xhr URLs, console.log arguments, UI clicks).
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console") {
        // console breadcrumbs can carry arbitrary payloads — keep level only.
        return { ...breadcrumb, message: "[console]", data: undefined };
      }
      if (breadcrumb.data && typeof breadcrumb.data === "object") {
        const data = { ...(breadcrumb.data as Record<string, unknown>) };
        if (typeof data.url === "string") data.url = scrubText(data.url);
        delete (data as Record<string, unknown>).body;
        delete (data as Record<string, unknown>).response_body_size_text;
        breadcrumb.data = data;
      }
      if (typeof breadcrumb.message === "string") {
        breadcrumb.message = scrubText(breadcrumb.message);
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.stacktrace?.frames) {
            for (const frame of ex.stacktrace.frames) {
              if (frame.filename?.startsWith("file://")) {
                frame.filename = "[local]";
              }
            }
          }
          if (ex.value) ex.value = scrubText(ex.value);
        }
      }
      if (event.message) event.message = scrubText(event.message);
      // Never let PII ride along in the user context: id only.
      if (event.user) {
        event.user = event.user.id ? { id: event.user.id } : undefined;
      }
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
        if (event.request.url) event.request.url = scrubText(event.request.url);
      }
      if (event.extra) event.extra = scrubExtras(event.extra);
      return event;
    },
  });
}

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const JWT_RE = /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}/g;
const TOKEN_QS_RE = /([?&](?:token|access_token|apikey|code|key)=)[^&#]+/gi;

/** Redacts e-mails, JWTs and token query params from any free-text value. */
function scrubText(input: string): string {
  return input
    .replace(EMAIL_RE, "[email]")
    .replace(JWT_RE, "[token]")
    .replace(TOKEN_QS_RE, "$1[redacted]");
}

const SENSITIVE_EXTRA_KEYS = /email|token|password|answer|question_text|content|prompt|explanation/i;

/** Keeps only low-cardinality technical extras; drops sensitive payloads. */
function scrubExtras(extras: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(extras)) {
    if (SENSITIVE_EXTRA_KEYS.test(key)) {
      out[key] = "[redacted]";
      continue;
    }
    out[key] = typeof value === "string" ? scrubText(value) : value;
  }
  return out;
}


function flushQueue(Sentry: SentryModule) {
  while (queue.length) {
    const call = queue.shift()!;
    try {
      if (call.kind === "captureError") {
        if (call.context) {
          Sentry.withScope((scope) => {
            scope.setExtras(call.context!);
            Sentry.captureException(call.error);
          });
        } else {
          Sentry.captureException(call.error);
        }
      } else if (call.kind === "setUser") {
        Sentry.setUser(call.userId ? { id: call.userId } : null);
      } else if (call.kind === "breadcrumb") {
        Sentry.addBreadcrumb({
          category: "navigation",
          message: `${call.from} → ${call.to}`,
          level: "info",
          data: { from: call.from, to: call.to },
        });
      } else if (call.kind === "customBreadcrumb") {
        Sentry.addBreadcrumb(call.payload);
      } else if (call.kind === "captureEvent") {
        Sentry.captureEvent({
          message: call.payload.message,
          level: call.payload.level,
          extra: call.payload.extra,
          tags: call.payload.tags,
        });
      }
    } catch (e) {
      console.warn("[Sentry] flush failed", e);
    }
  }
}

/**
 * Schedule Sentry SDK load AFTER first paint. Safe to call from main.tsx.
 * No-op when DSN not configured.
 */
export function initSentry() {
  if (isNativePlatform()) {
    console.info("[Sentry] Native platform (Capacitor) — error tracking disabled by design.");
    return;
  }
  if (!shouldLoad()) {
    console.info("[Sentry] No DSN configured — error tracking disabled.");
    return;
  }
  // Defer load until after the app has rendered. Prefer requestIdleCallback;
  // fall back to a short setTimeout for browsers without it.
  const schedule = (cb: () => void) => {
    const ric = (window as any).requestIdleCallback as undefined | ((fn: () => void, opts?: { timeout: number }) => number);
    if (typeof ric === "function") {
      ric(cb, { timeout: 3000 });
    } else {
      setTimeout(cb, 1500);
    }
  };
  schedule(() => {
    void loadSentry();
  });
}

/**
 * Privacy (FASE 1): only the opaque account id is sent to Sentry.
 * The `email` argument is accepted for call-site compatibility and
 * deliberately discarded — it is never forwarded.
 */
export function setSentryUser(userId: string | null, _email?: string | null) {
  if (sentryMod) {
    sentryMod.setUser(userId ? { id: userId } : null);
    return;
  }
  queue.push({ kind: "setUser", userId });
  void loadSentry();
}

export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (sentryMod) {
    if (context) {
      sentryMod.withScope((scope) => {
        scope.setExtras(context);
        sentryMod!.captureException(error);
      });
    } else {
      sentryMod.captureException(error);
    }
    return;
  }
  queue.push({ kind: "captureError", error, context });
  void loadSentry();
}

export function addNavigationBreadcrumb(from: string, to: string) {
  if (sentryMod) {
    sentryMod.addBreadcrumb({
      category: "navigation",
      message: `${from} → ${to}`,
      level: "info",
      data: { from, to },
    });
    return;
  }
  queue.push({ kind: "breadcrumb", from, to });
  // Breadcrumbs are low priority — don't trigger load just for these.
}

/**
 * Add a custom Sentry breadcrumb without forcing the SDK to load.
 * Use for diagnostic trails (e.g. access-engine decisions) that are
 * only useful when an error is later captured.
 */
export function addSentryBreadcrumb(payload: BreadcrumbPayload) {
  if (sentryMod) {
    sentryMod.addBreadcrumb(payload);
    return;
  }
  queue.push({ kind: "customBreadcrumb", payload });
  // Low priority — don't trigger SDK load just for a breadcrumb.
}

/**
 * Capture a synthetic Sentry event (non-exception). Triggers SDK load
 * because these events are usually high-signal alerts.
 */
export function captureSentryEvent(payload: EventPayload) {
  if (sentryMod) {
    sentryMod.captureEvent({
      message: payload.message,
      level: payload.level,
      extra: payload.extra,
      tags: payload.tags,
    });
    return;
  }
  queue.push({ kind: "captureEvent", payload });
  void loadSentry();
}
